import React, { useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { EnhancedButtonStatus } from '@components/button/EnhancedButton';
import ToastErrorMessage from '@components/toast/ToastErrorMessage';
import ToastSuccessMessage from '@components/toast/ToastSuccessMessage';
import { useMutation } from '@apollo/client';
import { getMarineInputs } from './content';
import { IMarineDetailsDrawerProps } from '.';
import { getError, lookupListAsRecordObject } from '@utils/graph-utils';
import { useParams } from 'react-router-dom';
import { updateMarine } from './queries';
import PrecisionService from '@services/PrecisionService';
import DateService from '@services/DateService';
import { calculateBasicRate } from '../../../../../../proposal/page/generic/queries';
import DynamicFormDrawer from '@components/dynamic-form/DynamicFormDrawer';
import { ICurrencyFormFieldProps } from '@components/dynamic-form/types/inputTypes';
import { normalizeDynamicFormDrawerValues } from '@components/dynamic-form/utils';
import { useAppSelector } from '@redux/hooks';

const MarineDetailsDrawer: React.FC<IMarineDetailsDrawerProps> = ({
  open,
  onClose,
  onSuccess,
  data,
  lovs,
}) => {
  const params = useParams();
  const { currency } = useAppSelector((state) => state.lovs);
  const currencySymbols = useMemo(() => {
    return lookupListAsRecordObject(currency, false, 'Code', 'Symbol');
  }, [currency]);
  const [formDisabled, setFormDisabled] = useState(false);
  const [submitButtonState, setSubmitButtonState] =
    useState<EnhancedButtonStatus>();

  const [saveMarineData] = useMutation(updateMarine());
  const [calculateBasicRateAction] = useMutation(calculateBasicRate());

  const inputForm = useMemo(() => getMarineInputs(lovs, data), [lovs, data]);

  const onCustomBlur = async (
    fieldName: string,
    values: Record<string, any>,
    errors: Record<string, any>
  ) => {
    if (
      fieldName === 'SumPerShipment' ||
      fieldName === 'SumPerShipmentCurrency'
    ) {
      try {
        const sumPerShipment = Number(values.SumPerShipment) || 0;
        const sumPerShipmentCurrency = values.SumPerShipmentCurrency;
        const policyCurrency = data?.PolicyCurrency?.Code;
        let newRate = 0;
        let newSumPolicy = 0;

        if (!sumPerShipmentCurrency) {
          newRate = 0;
          newSumPolicy = 0;
        } else if (sumPerShipmentCurrency === policyCurrency) {
          newRate = 1;
          newSumPolicy = PrecisionService.round(sumPerShipment);
        } else {
          const { data: sumCurrencyData } = await calculateBasicRateAction({
            variables: { currency: sumPerShipmentCurrency },
          });
          const { data: policyCurrencyData } = await calculateBasicRateAction({
            variables: { currency: policyCurrency },
          });
          const sumCurrencyBasicRate =
            sumCurrencyData?.production?.actions?.calculateBasicRate
              ?.BasicRate || 0;
          const policyCurrencyBasicRate =
            policyCurrencyData?.production?.actions?.calculateBasicRate
              ?.BasicRate || 0;
          newRate = sumCurrencyBasicRate / policyCurrencyBasicRate;
          newSumPolicy = PrecisionService.round(sumPerShipment * newRate);
        }
        values.SumPerShipmentRate = newRate;
        values.SumPerShipmentPolicyCurrency = newSumPolicy;
        (
          inputForm[0].inputs.SumPerShipment as ICurrencyFormFieldProps
        ).currencySymbol = currencySymbols[values.SumPerShipmentCurrency];
      } catch (err) {
        values.SumPerShipmentRate = 0;
        values.SumPerShipmentPolicyCurrency = 0;
        toast.error(<ToastErrorMessage>{getError(err)}</ToastErrorMessage>);
      }
    }
    return { values, errors };
  };

  const submitForm = async (formValues: Record<string, any>) => {
    setFormDisabled(true);
    setSubmitButtonState('loading');
    try {
      const [formData] = normalizeDynamicFormDrawerValues(
        inputForm,
        formValues
      );
      const policyMarineInput = {
        matter: formData.Matter,
        description: formData.Description,
        vesselName: formData.VesselName,
        vesselAge: Number(formData.VesselAge),
        departureFrom: formData.From,
        destinationTo: formData.To,
        transportationVia: formData.Via,
        survey: formData.Survey,
        cargoContract: formData.CargoContract,
        lCNumber: formData.LCNumber,
        lCIssueDate: DateService.formatDateBackend(formData.LCIssueDate),
        bank: formData.Bank,
        claimsPayableBy: formData.ClaimsPayableBy,
        loading: PrecisionService.divideBy100(formData.Loading),
        remarks: formData.Remarks,
        sumPerShipment: Number(formData.SumPerShipment),
        sumPerShipmentCurrency: formData.SumPerShipmentCurrency,
        sumPerShipmentRate: Number(formData.SumPerShipmentRate),
        sumPerShipmentPolicyCurrency: Number(
          formData.SumPerShipmentPolicyCurrency
        ),
      };

      const { errors } = await saveMarineData({
        variables: {
          entityId: params.id,
          policyMarineInput,
        },
      });

      if (!errors || errors.length === 0) {
        toast.success(
          <ToastSuccessMessage>
            Marine details updated successfully!
          </ToastSuccessMessage>
        );
        setSubmitButtonState('success');
        onSuccess();
        onClose();
      } else {
        setSubmitButtonState(undefined);
        const errorMessage = getError({ errors });
        toast.error(<ToastErrorMessage>{errorMessage}</ToastErrorMessage>);
      }
    } catch (err) {
      const errorMessage = getError({ err });
      toast.error(<ToastErrorMessage>{errorMessage}</ToastErrorMessage>);
      setSubmitButtonState(undefined);
    } finally {
      setFormDisabled(false);
    }
  };

  return (
    <DynamicFormDrawer
      title="Modify Marine Details"
      isOpen={!!open}
      onClose={onClose}
      loading={!data}
      sections={inputForm}
      onSubmit={submitForm}
      onCustomBlur={onCustomBlur}
      submitButtonState={submitButtonState}
      disableForm={formDisabled}
    />
  );
};

export default MarineDetailsDrawer;
